import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiBuilding, FiBatttery, FiSettings, FiTool, FiCheckCircle } = FiIcons;

function Services() {
  const services = [
    {
      icon: FiHome,
      title: "Residential Solar",
      description: "Complete solar solutions for homes including design, installation, and maintenance.",
      features: ["Custom system design", "Professional installation", "Monitoring system", "25-year warranty"],
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    },
    {
      icon: FiBuilding,
      title: "Commercial Solar",
      description: "Large-scale solar installations for businesses and commercial properties.",
      features: ["Scalable solutions", "Energy auditing", "ROI analysis", "Maintenance plans"],
      image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    },
    {
      icon: FiBatttery,
      title: "Battery Storage",
      description: "Energy storage solutions to maximize your solar investment and ensure energy independence.",
      features: ["Backup power", "Grid independence", "Peak shaving", "Smart monitoring"],
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    },
    {
      icon: FiSettings,
      title: "System Maintenance",
      description: "Comprehensive maintenance services to keep your solar system running at peak performance.",
      features: ["Regular inspections", "Performance monitoring", "Cleaning services", "Repair services"],
      image: "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    }
  ];

  const process = [
    {
      step: "01",
      title: "Consultation",
      description: "Free initial consultation to assess your energy needs and site conditions."
    },
    {
      step: "02",
      title: "Design",
      description: "Custom system design optimized for your property and energy requirements."
    },
    {
      step: "03",
      title: "Installation",
      description: "Professional installation by certified technicians with minimal disruption."
    },
    {
      step: "04",
      title: "Activation",
      description: "System activation, testing, and connection to the grid for immediate savings."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Solar Services - SolarTech Solutions | Residential & Commercial Solar Installation</title>
        <meta name="description" content="Comprehensive solar services including residential and commercial installation, battery storage, and maintenance. Professional solar solutions with 25-year warranty." />
        <meta name="keywords" content="solar installation services, residential solar, commercial solar, battery storage, solar maintenance, solar panel installation" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 to-orange-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Solar Services</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              From initial consultation to ongoing maintenance, we provide comprehensive solar energy 
              solutions tailored to your specific needs. Our expert team ensures quality installation 
              and maximum energy savings for your property.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center">
                      <SafeIcon icon={service.icon} className="text-white text-2xl" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-800">{service.title}</h2>
                  </div>
                  
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <SafeIcon icon={FiCheckCircle} className="text-green-500 text-lg flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button className="mt-8 bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transition-shadow">
                    Learn More
                  </button>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-2xl shadow-lg w-full h-80 object-cover"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Our Installation Process
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We follow a streamlined process to ensure your solar installation is completed 
              efficiently and to the highest standards
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-white text-2xl font-bold">{step.step}</span>
                  </div>
                  {index < process.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-orange-500 to-yellow-500 transform -translate-y-1/2"></div>
                  )}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Contact us today for a free consultation and custom quote for your solar energy system. 
              Our experts will help you choose the perfect solution for your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-orange-500 px-8 py-4 rounded-full font-semibold hover:shadow-lg transition-shadow">
                Get Free Quote
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-500 transition-colors">
                Call Us Now
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}

export default Services;